import { themeColor } from '@hud/web-components/theme/colors';
import React from 'react';
import styled from 'styled-components';
import { isEndpointEntity, Issue } from '@hud/portal-dtos/issues.dtos';
import useEndpointSettingsMutation from '../../hooks/useEndpointSettingsMutation';
import { EndpointSettingsAction } from '@hud/portal-dtos/issues.dtos';
import {
  NotificationType,
  showNotification,
} from '../ToastNotification/ToastNotification';
import { Link } from '../Link/Link';
import HeadsUpIssueMenu from '@hud/web-components/Hud/HeadsUpIssueMenu/HeadsUpIssueMenu';
import { PublicIssue } from '@hud/portal-dtos/issues-v2.dtos';
import { useNavigate } from 'react-router-dom';
import { useGlobalQueryString } from '../../hooks/useGlobalFilter';

export const SettingsUpdatedErrorMessage =
  'Problem occurred while updating settings.';

interface IssuesMenuWebappContainerProps {
  issue: Issue | PublicIssue;
  filterType?: 'endpoint';
}

export default function IssuesMenuWebappContainer({
  issue,
  filterType,
}: IssuesMenuWebappContainerProps) {
  const navigate = useNavigate();
  const globalFilters = useGlobalQueryString();
  const endpointSettingsMutation = useEndpointSettingsMutation();

  const isMuted = !!(
    isEndpointEntity(issue.mainEntity) && issue.mainEntity.isMuted
  );

  const isDetectionExcluded = !!(
    isEndpointEntity(issue.mainEntity) && issue.mainEntity.isDetectionExcluded
  );

  async function handleAction(action: EndpointSettingsAction) {
    const endpointId = issue.mainEntity.id;
    const serviceId = issue.serviceId;

    try {
      await endpointSettingsMutation.mutateAsync({
        endpointId,
        serviceId,
        action,
      });
      showNotification({
        type: NotificationType.SUCCESS,
        message: <SettingsUpdatedSuccessfullyMessage />,
      });
    } catch (error) {
      showNotification({
        type: NotificationType.ERROR,
        message: SettingsUpdatedErrorMessage,
      });
    }
  }

  async function toggleMute() {
    const action = isMuted
      ? EndpointSettingsAction.UNMUTE
      : EndpointSettingsAction.MUTE;

    await handleAction(action);
  }

  async function toggleMonitoring() {
    const action = isDetectionExcluded
      ? EndpointSettingsAction.INCLUDE_DETECTION
      : EndpointSettingsAction.EXCLUDE_DETECTION;

    await handleAction(action);
  }

  const getRuleId = (issue: Issue | PublicIssue): string | undefined => {
    if ('ruleId' in issue) {
      return issue.ruleId;
    }
    return undefined;
  };

  return (
    <HeadsUpIssueMenu
      isMuted={isMuted}
      isDetectionExcluded={isDetectionExcluded}
      isEditableRule={!!getRuleId(issue)}
      toggleMute={toggleMute}
      toggleMonitoring={toggleMonitoring}
      editRule={() => {
        const ruleId = getRuleId(issue);
        if (ruleId) {
          navigate(`/heads-up/rules/${ruleId}?${globalFilters}`);
        }
      }}
      filterType={filterType}
    />
  );
}

export function SettingsUpdatedSuccessfullyMessage({
  onClick,
}: {
  onClick?: () => void;
}) {
  return (
    <NotificationContainer>
      Settings were updated successfully.
      <br />
      <Link onClick={onClick} to={`/settings/heads-up`}>
        Manage settings
      </Link>
    </NotificationContainer>
  );
}

const NotificationContainer = styled.div`
  white-space: nowrap;
  color: ${themeColor('textPrimary')};
  & a {
    color: ${themeColor('textPrimary')};
  }
`;
